import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import {
  addNotification,
  clearAll,
  deleteNotification,
  markAllAsRead,
  markAsRead,
  markAsUnread,
  seedDevNotifications,
} from '../utils/notifications';
import Button from '../components/common/Button';
import {
  BellIcon,
  CalendarDaysIcon,
  StarIcon,
  CreditCardIcon,
  UserCircleIcon,
  GiftIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EllipsisHorizontalIcon,
  ListBulletIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PER_PAGE = 10;

const CATEGORY_META = {
  booking:   { label: 'Booking',   icon: CalendarDaysIcon, color: '#3b82f6', bg: 'var(--blue-3, #dbeafe)' },
  review:    { label: 'Review',    icon: StarIcon,         color: '#eab308', bg: '#fef9c3' },
  payment:   { label: 'Payment',   icon: CreditCardIcon,   color: '#22c55e', bg: 'var(--green-3, #dcfce7)' },
  account:   { label: 'Account',   icon: UserCircleIcon,   color: 'var(--orange-9)', bg: 'var(--orange-3)' },
  promotion: { label: 'Promotion', icon: GiftIcon,         color: '#a855f7', bg: '#f3e8ff' },
  system:    { label: 'System',    icon: BellIcon,         color: 'var(--gray-10)', bg: 'var(--gray-3)' },
};

const TYPE_DOT = {
  success: '#22c55e',
  info:    '#3b82f6',
  warning: 'var(--orange-9)',
  error:   'var(--red-9, #e5484d)',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Notifications = () => {
  const { notifications, unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, activeTab]);

  // Close any open row menu when clicking outside
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (!e.target.closest?.('[data-row-menu]')) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const counts = useMemo(() => {
    const c = { all: notifications.length, unread: 0, booking: 0, review: 0, payment: 0, account: 0, promotion: 0 };
    notifications.forEach(n => {
      if (!n.readAt) c.unread++;
      if (c[n.category] != null) c[n.category]++;
    });
    return c;
  }, [notifications]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notifications.filter(n => {
      if (activeTab === 'unread' && n.readAt) return false;
      if (activeTab !== 'all' && activeTab !== 'unread' && n.category !== activeTab) return false;
      if (q) {
        const t = (n.title || '').toLowerCase();
        const b = (n.body || '').toLowerCase();
        if (!t.includes(q) && !b.includes(q)) return false;
      }
      return true;
    });
  }, [notifications, activeTab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleClickRow = (n) => {
    if (!n.readAt) markAsRead(n.id);
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = () => {
    if (unreadCount === 0) return;
    markAllAsRead();
    toast.success('All notifications marked as read');
  };

  const handleClearAll = () => {
    if (notifications.length === 0) return;
    if (!window.confirm('Remove all notifications? This cannot be undone.')) return;
    clearAll();
    toast.success('All notifications cleared');
  };

  const handleSeed = () => {
    seedDevNotifications();
    toast.success('Sample notifications added');
  };

  const handleTestPush = () => {
    addNotification({
      type: 'info',
      category: 'system',
      title: 'Test notification',
      body: 'This is a sample notification to preview formatting.',
    });
  };

  const tabs = [
    { id: 'all',       label: 'All',         icon: ListBulletIcon,    count: counts.all },
    { id: 'unread',    label: 'Unread',      icon: InboxIcon,         count: counts.unread },
    { id: 'booking',   label: 'Bookings',    icon: CalendarDaysIcon,  count: counts.booking },
    { id: 'review',    label: 'Reviews',     icon: StarIcon,          count: counts.review },
    { id: 'payment',   label: 'Payments',    icon: CreditCardIcon,    count: counts.payment },
    { id: 'account',   label: 'Account',     icon: UserCircleIcon,    count: counts.account },
    { id: 'promotion', label: 'Promotions',  icon: GiftIcon,          count: counts.promotion },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Notifications</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Stay on top of your bookings, reviews, payments, and account activity
          </p>
        </div>

        {/* Banner */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
        >
          <BellIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', flex: 1, textAlign: 'left' }}>
            {unreadCount === 0
              ? "You're all caught up"
              : `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
          </span>
          <Link
            to="/settings"
            style={{ fontSize: '13px', color: 'var(--orange-9)', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}
          >
            Notification settings →
          </Link>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-6 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '15px', height: '15px' }} />
              {label}
              {count > 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: 600,
                  padding: '1px 7px', borderRadius: '999px',
                  backgroundColor: activeTab === id ? 'var(--orange-3)' : 'var(--gray-4)',
                  color: activeTab === id ? 'var(--orange-11)' : 'var(--gray-11)',
                }}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content card */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {/* Action row: search + bulk actions */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
              <MagnifyingGlassIcon style={{
                position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                width: '16px', height: '16px', color: 'var(--gray-9)', pointerEvents: 'none',
              }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notifications..."
                style={{
                  width: '100%', padding: '9px 36px 9px 38px', borderRadius: '10px',
                  border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                  color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{
                    position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                    padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer',
                    display: 'flex',
                  }}
                >
                  <XMarkIcon style={{ width: '16px', height: '16px', color: 'var(--gray-9)' }} />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              style={{
                padding: '8px 12px', borderRadius: '8px',
                border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                color: 'var(--gray-11)', fontSize: '13px', fontWeight: 500,
                cursor: unreadCount === 0 ? 'not-allowed' : 'pointer',
                opacity: unreadCount === 0 ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
              Mark all read
            </button>

            <button
              type="button"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
              style={{
                padding: '8px 12px', borderRadius: '8px',
                border: '1px solid var(--red-6, #fca5a5)', backgroundColor: 'transparent',
                color: 'var(--red-9, #e5484d)', fontSize: '13px', fontWeight: 500,
                cursor: notifications.length === 0 ? 'not-allowed' : 'pointer',
                opacity: notifications.length === 0 ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <TrashIcon style={{ width: '14px', height: '14px' }} />
              Clear all
            </button>
          </div>

          {/* Empty state */}
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                backgroundColor: 'var(--gray-3)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
              }}>
                <BellIcon style={{ width: '28px', height: '28px', color: 'var(--gray-9)' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px' }}>
                No notifications yet
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px' }}>
                We will let you know when something happens with your bookings, reviews, or account.
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="secondary" size="small" onClick={handleSeed}>
                  Add sample notifications
                </Button>
                <Button size="small" onClick={handleTestPush}>
                  Send test
                </Button>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 rounded-lg" style={{ backgroundColor: 'var(--color-background)', border: '1px dashed var(--gray-5)' }}>
              <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                No notifications match your filters.
              </p>
              {(search || activeTab !== 'all') && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setActiveTab('all'); }}
                  style={{
                    marginTop: '8px', padding: '6px 14px', borderRadius: '8px',
                    border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                    color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Reset filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Result count */}
              <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '0 0 10px', textAlign: 'left' }}>
                Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
              </p>

              {/* List */}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pageItems.map(n => {
                  const meta = CATEGORY_META[n.category] || CATEGORY_META.system;
                  const Icon = meta.icon;
                  const dot = TYPE_DOT[n.type] || TYPE_DOT.info;
                  const unread = !n.readAt;
                  return (
                    <li
                      key={n.id}
                      onClick={() => handleClickRow(n)}
                      className="rounded-lg flex items-start gap-3"
                      style={{
                        padding: '14px',
                        backgroundColor: unread ? 'var(--orange-2)' : 'var(--color-background)',
                        border: `1px solid ${unread ? 'var(--orange-4)' : 'var(--gray-4)'}`,
                        cursor: n.link ? 'pointer' : 'default',
                        position: 'relative',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Category icon */}
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '10px',
                        backgroundColor: meta.bg, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <Icon style={{ width: '20px', height: '20px', color: meta.color }} />
                      </div>

                      {/* Body */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex items-center gap-2" style={{ marginBottom: '2px' }}>
                          {unread && (
                            <span style={{
                              width: '8px', height: '8px', borderRadius: '50%',
                              backgroundColor: dot, flexShrink: 0,
                            }} />
                          )}
                          <p style={{
                            fontSize: '14px', fontWeight: unread ? 600 : 500,
                            color: 'var(--gray-12)', margin: 0, textAlign: 'left', flex: 1,
                          }}>
                            {n.title}
                          </p>
                          <span style={{ fontSize: '12px', color: 'var(--gray-10)', flexShrink: 0 }}>
                            {formatRelative(n.createdAt)}
                          </span>
                        </div>
                        {n.body && (
                          <p style={{ fontSize: '13px', color: 'var(--gray-11)', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
                            {n.body}
                          </p>
                        )}
                        <div className="flex items-center gap-2" style={{ marginTop: '6px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '1px 8px', borderRadius: '999px',
                              fontSize: '11px', fontWeight: 600,
                              backgroundColor: meta.bg, color: meta.color,
                              textTransform: 'capitalize',
                            }}
                          >
                            {meta.label}
                          </span>
                          {n.link && (
                            <span style={{ fontSize: '12px', color: 'var(--orange-9)', fontWeight: 500 }}>
                              View →
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Row menu */}
                      <div data-row-menu style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenu(openMenu === n.id ? null : n.id);
                          }}
                          style={{
                            width: '28px', height: '28px', borderRadius: '6px',
                            border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <EllipsisHorizontalIcon style={{ width: '18px', height: '18px', color: 'var(--gray-10)' }} />
                        </button>
                        {openMenu === n.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute', top: '32px', right: 0, zIndex: 5,
                              minWidth: '160px', borderRadius: '10px',
                              backgroundColor: 'var(--color-background)',
                              border: '1px solid var(--gray-4)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                              padding: '4px',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => { unread ? markAsRead(n.id) : markAsUnread(n.id); setOpenMenu(null); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                width: '100%', padding: '8px 10px', borderRadius: '6px',
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                fontSize: '13px', color: 'var(--gray-12)', textAlign: 'left',
                              }}
                            >
                              <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                              {unread ? 'Mark as read' : 'Mark as unread'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { deleteNotification(n.id); setOpenMenu(null); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                width: '100%', padding: '8px 10px', borderRadius: '6px',
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                fontSize: '13px', color: 'var(--red-9, #e5484d)', textAlign: 'left',
                              }}
                            >
                              <TrashIcon style={{ width: '14px', height: '14px' }} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Pagination */}
              {filtered.length > PER_PAGE && (
                <div className="flex items-center justify-between mt-5 pt-4" style={{ borderTop: '1px solid var(--gray-4)' }}>
                  <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === 1 ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === 1 ? 0.4 : 1,
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '...' ? (
                        <span key={`gap-${i}`} style={{ padding: '0 6px', color: 'var(--gray-9)', fontSize: '13px', alignSelf: 'center' }}>…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          style={{
                            width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                            fontSize: '13px', fontWeight: page === p ? 600 : 400, cursor: 'pointer',
                            backgroundColor: page === p ? 'var(--orange-9)' : 'var(--gray-4)',
                            color: page === p ? '#fff' : 'var(--gray-11)',
                          }}
                        >
                          {p}
                        </button>
                      ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === totalPages ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === totalPages ? 0.4 : 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
